import Errors from './Errors';
import {cloneDeep, isEqual} from 'lodash';

class Form {

    /**
     * Create a new Form instance.
     *
     * @param {object} data
     * @param {string} scope
     */
    constructor(data, scope) {
        this.originalData = data;
        this.scope = scope;
        this.data = cloneDeep(data);
        this.beforeSubmit = null;
        this.errors = new Errors();
    }

    /**
     * Add a data input.
     *
     * @param {string} key
     * @param {string} value
     */
    addData(key, value) {
        this.originalData[key] = value;
        this.data[key] = value;
    }

    /**
     * Submit the form.
     *
     * @param {string} requestType
     * @param {string} url
     * @param {boolean} sendAsFormData
     *
     * @return {Promise}
     */
    submit(requestType, url, sendAsFormData = false) {
        if (this.beforeSubmit) {
            this.beforeSubmit(this);
        }

        let formData = this.data;
        if (typeof sendAsFormData !== 'undefined' && sendAsFormData) {
            formData = this.convertToFormData();

            if (['put', 'patch'].includes(requestType.toLowerCase())) {
                formData.append('_method', requestType.toUpperCase());
                requestType = 'post';
            }
        }

        return new Promise((resolve, reject) => {
            axios[requestType](url, formData, {headers: (this.headers || {})}).then(response => {
                this.onSuccess(response.data);
                resolve(response.data);
            }).catch(error => {
                this.onFail(error.response.data);
                reject({error: error.response.data, errors: this.errors.toArray()});
            });
        });
    }

    /**
     * Form was successful so handle this.
     *
     * @param {object} data
     */
    onSuccess(data) {
        this.errors.remove();
    }

    /**
     * Form failed so handle this.
     *
     * @param {object} errors
     */
    onFail(errors) {
        if (!errors.hasOwnProperty('errors')) {
            errors.errors = [
                {
                    error: 'Something went wrong.',
                },
            ];
        }

        this.errors.set(errors.errors);
    }

    /**
     * Convert the data to a FormData object.
     *
     * @returns {FormData}
     */
    convertToFormData() {
        let formData = new FormData();

        for (let field in this.data) {
            let data = this.data[field] === null ? '' : this.data[field];

            if (Array.isArray(data)) {
                for (let i = 0; i < data.length; i++) {
                    formData.append(field + '[]', data[i]);
                }
                continue;
            }

            formData.append(field, data);
        }

        return formData;
    }

    /**
     * Reset the form to it's original state.
     *
     * @param {string} field Field to remove errors for (optional)
     */
    reset(field) {

        // Single field
        if (field) {
            this.data[field] = '';
            this.errors.remove(field);

            return;
        }

        // All fields
        this.data = cloneDeep(this.originalData);

        this.errors.remove();
    }

    /**
     * Get scope of form.
     *
     * @returns {string}
     */
    getScope() {
        return this.scope;
    }

    /**
     * Determine whether the form is dirty (if any of the fields have been modified)
     */
    isDirty() {
        return !isEqual(this.data, this.originalData);
    }

    /**
     * Refresh the form so that the original data is set to the current data.
     */
    refresh() {
        this.originalData = cloneDeep(this.data);
    }

    /**
     * Set customer header information.
     *
     * @param headers
     */
    setHeaders(headers) {
        this.headers = headers;
    }

    /**
     * Set a callback to be called before the form is submitted.
     *
     * @param {function} callback
     */
    setBeforeSubmitCallback(callback) {
        this.beforeSubmit = callback;
    }
}

export default Form;
